import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
// import { RecurringPayment, OneOffPayment } from '../types';
import { formatCurrency, formatDate, formatDateShort, getDaysUntilPayment } from '../utils/calculations';
import CreditCardManager from './CreditCardManager';

const PaymentManager: React.FC = () => {
  const { dispatch } = useAppContext();
  const [activeTab, setActiveTab] = useState<'recurring' | 'oneoff' | 'creditcards'>('recurring');
  const [recurringShowAddRow, setRecurringShowAddRow] = useState(false);
  const [oneoffShowAddRow, setOneoffShowAddRow] = useState(false);
  const [creditCardShowAddRow, setCreditCardShowAddRow] = useState(false);

  const handleAddPayment = () => {
    if (activeTab === 'recurring') {
      setRecurringShowAddRow(true);
    } else if (activeTab === 'oneoff') {
      setOneoffShowAddRow(true);
    } else if (activeTab === 'creditcards') {
      setCreditCardShowAddRow(true);
    }
  };

  const isAddDisabled = () => {
    if (activeTab === 'recurring') return recurringShowAddRow;
    if (activeTab === 'oneoff') return oneoffShowAddRow;
    return creditCardShowAddRow;
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-800">Payment Management</h2>

        <div className="flex justify-between items-center mb-6">
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('recurring')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                activeTab === 'recurring'
                  ? 'btn-primary'
                  : 'btn-secondary'
              }`}
            >
              Recurring Payments
            </button>
            <button
              onClick={() => setActiveTab('oneoff')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                activeTab === 'oneoff'
                  ? 'btn-primary'
                  : 'btn-secondary'
              }`}
            >
              One-Off Payments
            </button>
            <button
              onClick={() => setActiveTab('creditcards')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                activeTab === 'creditcards'
                  ? 'btn-primary'
                  : 'btn-secondary'
              }`}
            >
              Credit Cards
            </button>
          </div>
          <button
            onClick={handleAddPayment}
            className="btn-primary"
            disabled={isAddDisabled()}
          >
            {activeTab === 'creditcards' ? 'Add Credit Card' : 'Add Payment'}
          </button>
        </div>

        {activeTab === 'recurring' ? (
          <RecurringPaymentsList
            showAddRow={recurringShowAddRow}
            setShowAddRow={setRecurringShowAddRow}
          />
        ) : activeTab === 'oneoff' ? (
          <OneOffPaymentsList
            showAddRow={oneoffShowAddRow}
            setShowAddRow={setOneoffShowAddRow}
          />
        ) : (
          <CreditCardManager
            showAddForm={creditCardShowAddRow}
            setShowAddForm={setCreditCardShowAddRow}
          />
        )}
      </div>
    </div>
  );
};

interface RecurringPaymentsListProps {
  showAddRow: boolean;
  setShowAddRow: (show: boolean) => void;
}

const RecurringPaymentsList: React.FC<RecurringPaymentsListProps> = ({ showAddRow, setShowAddRow }) => {
  const { state, dispatch } = useAppContext();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    description: '',
    amount: '',
    dayOfMonth: '',
    type: 'expense' as 'income' | 'expense',
    startDate: '',
    endDate: ''
  });
  const [addForm, setAddForm] = useState({
    description: '',
    amount: '',
    dayOfMonth: '1',
    type: 'expense' as 'income' | 'expense',
    startDate: '',
    endDate: ''
  });

  const togglePayment = (id: string, isActive: boolean) => {
    dispatch({
      type: 'UPDATE_RECURRING_PAYMENT',
      payload: { id, payment: { isActive } }
    });
  };

  const deletePayment = (id: string) => {
    if (window.confirm('Are you sure you want to delete this payment?')) {
      dispatch({ type: 'DELETE_RECURRING_PAYMENT', payload: id });
    }
  };

  const startEditing = (payment: any) => {
    setEditingId(payment.id);
    setEditForm({
      description: payment.description,
      amount: payment.amount.toString(),
      dayOfMonth: payment.dayOfMonth.toString(),
      type: payment.type,
      startDate: payment.startDate ? payment.startDate.toISOString().split('T')[0] : '',
      endDate: payment.endDate ? payment.endDate.toISOString().split('T')[0] : ''
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({
      description: '',
      amount: '',
      dayOfMonth: '',
      type: 'expense',
      startDate: '',
      endDate: ''
    });
  };

  const saveEdit = () => {
    if (editingId) {
      dispatch({
        type: 'UPDATE_RECURRING_PAYMENT',
        payload: {
          id: editingId,
          payment: {
            description: editForm.description,
            amount: parseFloat(editForm.amount),
            dayOfMonth: editForm.dayOfMonth === 'LDOM' ? 'LDOM' : parseInt(editForm.dayOfMonth),
            type: editForm.type,
            startDate: editForm.startDate ? new Date(editForm.startDate) : undefined,
            endDate: editForm.endDate ? new Date(editForm.endDate) : undefined
          }
        }
      });
      cancelEditing();
    }
  };

  const cancelAdding = () => {
    setShowAddRow(false);
    setAddForm({
      description: '',
      amount: '',
      dayOfMonth: '1',
      type: 'expense',
      startDate: '',
      endDate: ''
    });
  };

  const saveNew = () => {
    if (addForm.description && addForm.amount) {
      dispatch({
        type: 'ADD_RECURRING_PAYMENT',
        payload: {
          description: addForm.description,
          amount: parseFloat(addForm.amount),
          dayOfMonth: addForm.dayOfMonth === 'LDOM' ? 'LDOM' : parseInt(addForm.dayOfMonth),
          type: addForm.type,
          isActive: true,
          startDate: addForm.startDate ? new Date(addForm.startDate) : undefined,
          endDate: addForm.endDate ? new Date(addForm.endDate) : undefined
        }
      });
      cancelAdding();
    }
  };

  return (
    <div>
      {state.recurringPayments.length === 0 && !showAddRow ? (
        <p className="text-gray-500 text-center py-8">No recurring payments configured</p>
      ) : (
        <div className="table-container payments-table">
          <table className="data-table">
            <thead>
              <tr>
                <th className="text-left">Description</th>
                <th className="text-right">Amount</th>
                <th className="text-center">Day</th>
                <th className="text-center">Next Payment</th>
                <th className="text-center">Start Date</th>
                <th className="text-center">End Date</th>
                <th className="text-center">Type</th>
                <th className="text-center">Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {state.recurringPayments
                .sort((a, b) => {
                  // Handle sorting by day of month: 1-31, then LDOM (last day)
                  const dayA = a.dayOfMonth === 'LDOM' ? 32 : a.dayOfMonth;
                  const dayB = b.dayOfMonth === 'LDOM' ? 32 : b.dayOfMonth;
                  return (dayA as number) - (dayB as number);
                })
                .map(payment => (
                <tr key={payment.id}>
                  {editingId === payment.id ? (
                    <>
                      <td className="text-left">
                        <input
                          type="text"
                          value={editForm.description}
                          onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                      </td>
                      <td className="text-right">
                        <input
                          type="number"
                          step="0.01"
                          value={editForm.amount}
                          onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded text-right"
                        />
                      </td>
                      <td className="text-center">
                        <select
                          value={editForm.dayOfMonth}
                          onChange={(e) => setEditForm({ ...editForm, dayOfMonth: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        >
                          {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                            <option key={day} value={day.toString()}>
                              {day}
                            </option>
                          ))}
                          <option value="LDOM">Last Day</option>
                        </select>
                      </td>
                      <td className="text-center text-gray-600">{getDaysUntilPayment(payment)} days</td>
                      <td className="text-center">
                        <input
                          type="date"
                          value={editForm.startDate || ''}
                          onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })}
                          className="w-32 px-2 py-1 text-sm border border-gray-300 rounded"
                          placeholder="Optional"
                        />
                      </td>
                      <td className="text-center">
                        <input
                          type="date"
                          value={editForm.endDate || ''}
                          onChange={(e) => setEditForm({ ...editForm, endDate: e.target.value })}
                          className="w-32 px-2 py-1 text-sm border border-gray-300 rounded"
                          placeholder="Optional"
                        />
                      </td>
                      <td className="text-center">
                        <select
                          value={editForm.type}
                          onChange={(e) => setEditForm({ ...editForm, type: e.target.value as 'income' | 'expense' })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        >
                          <option value="expense">💸 Expense</option>
                          <option value="income">💰 Income</option>
                        </select>
                      </td>
                      <td className="text-center">
                        <label className="flex items-center justify-center text-sm">
                          <input
                            type="checkbox"
                            checked={payment.isActive}
                            onChange={(e) => togglePayment(payment.id, e.target.checked)}
                            className="mr-1"
                          />
                          Active
                        </label>
                      </td>
                      <td className="text-center">
                        <div className="flex space-x-1 justify-center">
                          <button
                            onClick={saveEdit}
                            className="btn-secondary text-xs px-2 py-1"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="btn-neutral text-xs px-2 py-1"
                          >
                            Cancel
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="text-left font-medium text-gray-800">{payment.description}</td>
                      <td className={`text-right font-semibold ${payment.type === 'income' ? 'balance-positive' : 'balance-negative'}`}>
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="text-center text-gray-600">
                        {payment.dayOfMonth === 'LDOM' ? 'Last Day' : payment.dayOfMonth}
                      </td>
                      <td className="text-center text-gray-600">{getDaysUntilPayment(payment)} days</td>
                      <td className="text-center text-gray-600">
                        {payment.startDate ? formatDateShort(payment.startDate) : 'Always'}
                      </td>
                      <td className="text-center text-gray-600">
                        {payment.endDate ? formatDateShort(payment.endDate) : 'Never'}
                      </td>
                      <td className="text-center">
                        <span className={`pill ${
                          payment.type === 'income'
                            ? 'pill-success'
                            : 'pill-error'
                        }`}>
                          {payment.type === 'income' ? '💰 Income' : '💸 Expense'}
                        </span>
                      </td>
                      <td className="text-center">
                        <label className="flex items-center justify-center text-sm">
                          <input
                            type="checkbox"
                            checked={payment.isActive}
                            onChange={(e) => togglePayment(payment.id, e.target.checked)}
                            className="mr-1"
                          />
                          Active
                        </label>
                      </td>
                      <td className="text-center">
                        <div className="flex space-x-1 justify-center">
                          <button
                            onClick={() => startEditing(payment)}
                            className="btn-secondary text-xs px-2 py-1"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deletePayment(payment.id)}
                            className="btn-danger text-xs px-2 py-1"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {showAddRow && (
                <tr>
                  <td className="text-left">
                    <input
                      type="text"
                      placeholder="Payment description"
                      value={addForm.description}
                      onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </td>
                  <td className="text-right">
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={addForm.amount}
                      onChange={(e) => setAddForm({ ...addForm, amount: e.target.value })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded text-right"
                    />
                  </td>
                  <td className="text-center">
                    <select
                      value={addForm.dayOfMonth}
                      onChange={(e) => setAddForm({ ...addForm, dayOfMonth: e.target.value })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    >
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                        <option key={day} value={day.toString()}>
                          {day}
                        </option>
                      ))}
                      <option value="LDOM">Last Day</option>
                    </select>
                  </td>
                  <td className="text-center text-gray-500">-</td>
                  <td className="text-center">
                    <input
                      type="date"
                      value={addForm.startDate}
                      onChange={(e) => setAddForm({ ...addForm, startDate: e.target.value })}
                      className="w-32 px-2 py-1 text-sm border border-gray-300 rounded"
                      placeholder="Optional"
                    />
                  </td>
                  <td className="text-center">
                    <input
                      type="date"
                      value={addForm.endDate}
                      onChange={(e) => setAddForm({ ...addForm, endDate: e.target.value })}
                      className="w-32 px-2 py-1 text-sm border border-gray-300 rounded"
                      placeholder="Optional"
                    />
                  </td>
                  <td className="text-center">
                    <select
                      value={addForm.type}
                      onChange={(e) => setAddForm({ ...addForm, type: e.target.value as 'income' | 'expense' })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    >
                      <option value="expense">💸 Expense</option>
                      <option value="income">💰 Income</option>
                    </select>
                  </td>
                  <td className="text-center">
                    <span className="text-xs text-gray-500">Active</span>
                  </td>
                  <td className="text-center">
                    <div className="flex space-x-1 justify-center">
                      <button
                        onClick={saveNew}
                        className="btn-secondary text-xs px-2 py-1"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelAdding}
                        className="btn-neutral text-xs px-2 py-1"
                      >
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

interface OneOffPaymentsListProps {
  showAddRow: boolean;
  setShowAddRow: (show: boolean) => void;
}

const OneOffPaymentsList: React.FC<OneOffPaymentsListProps> = ({ showAddRow, setShowAddRow }) => {
  const { state, dispatch } = useAppContext();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    description: '',
    amount: '',
    date: '',
    type: 'expense' as 'income' | 'expense'
  });
  const [addForm, setAddForm] = useState({
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    type: 'expense' as 'income' | 'expense'
  });

  const deletePayment = (id: string) => {
    if (window.confirm('Are you sure you want to delete this payment?')) {
      dispatch({ type: 'DELETE_ONE_OFF_PAYMENT', payload: id });
    }
  };

  const startEditing = (payment: any) => {
    setEditingId(payment.id);
    setEditForm({
      description: payment.description,
      amount: payment.amount.toString(),
      date: payment.date.toISOString().split('T')[0],
      type: payment.type
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({
      description: '',
      amount: '',
      date: '',
      type: 'expense'
    });
  };

  const saveEdit = () => {
    if (editingId) {
      dispatch({
        type: 'UPDATE_ONE_OFF_PAYMENT',
        payload: {
          id: editingId,
          payment: {
            description: editForm.description,
            amount: parseFloat(editForm.amount),
            date: new Date(editForm.date),
            type: editForm.type
          }
        }
      });
      cancelEditing();
    }
  };

  const cancelAdding = () => {
    setShowAddRow(false);
    setAddForm({
      description: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      type: 'expense'
    });
  };

  const saveNew = () => {
    if (addForm.description && addForm.amount && addForm.date) {
      dispatch({
        type: 'ADD_ONE_OFF_PAYMENT',
        payload: {
          description: addForm.description,
          amount: parseFloat(addForm.amount),
          date: new Date(addForm.date),
          type: addForm.type
        }
      });
      cancelAdding();
    }
  };

  return (
    <div>
      {state.oneOffPayments.length === 0 && !showAddRow ? (
        <p className="text-gray-500 text-center py-8">No one-off payments configured</p>
      ) : (
        <div className="table-container payments-table">
          <table className="data-table">
            <thead>
              <tr>
                <th className="text-left">Description</th>
                <th className="text-right">Amount</th>
                <th className="text-center">Date</th>
                <th className="text-center">Type</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {state.oneOffPayments.map(payment => (
                <tr key={payment.id}>
                  {editingId === payment.id ? (
                    <>
                      <td className="text-left">
                        <input
                          type="text"
                          value={editForm.description}
                          onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                      </td>
                      <td className="text-right">
                        <input
                          type="number"
                          step="0.01"
                          value={editForm.amount}
                          onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded text-right"
                        />
                      </td>
                      <td className="text-center">
                        <input
                          type="date"
                          value={editForm.date}
                          onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                      </td>
                      <td className="text-center">
                        <select
                          value={editForm.type}
                          onChange={(e) => setEditForm({ ...editForm, type: e.target.value as 'income' | 'expense' })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        >
                          <option value="expense">💸 Expense</option>
                          <option value="income">💰 Income</option>
                        </select>
                      </td>
                      <td className="text-center">
                        <div className="flex space-x-1 justify-center">
                          <button
                            onClick={saveEdit}
                            className="btn-secondary text-xs px-2 py-1"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="btn-neutral text-xs px-2 py-1"
                          >
                            Cancel
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="text-left font-medium text-gray-800">{payment.description}</td>
                      <td className={`text-right font-semibold ${payment.type === 'income' ? 'balance-positive' : 'balance-negative'}`}>
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="text-center text-gray-600">{formatDate(payment.date)}</td>
                      <td className="text-center">
                        <span className={`pill ${
                          payment.type === 'income'
                            ? 'pill-success'
                            : 'pill-error'
                        }`}>
                          {payment.type === 'income' ? '💰 Income' : '💸 Expense'}
                        </span>
                      </td>
                      <td className="text-center">
                        <div className="flex space-x-1 justify-center">
                          <button
                            onClick={() => startEditing(payment)}
                            className="btn-secondary text-xs px-2 py-1"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deletePayment(payment.id)}
                            className="btn-danger text-xs px-2 py-1"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {showAddRow && (
                <tr>
                  <td className="text-left">
                    <input
                      type="text"
                      placeholder="Payment description"
                      value={addForm.description}
                      onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </td>
                  <td className="text-right">
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={addForm.amount}
                      onChange={(e) => setAddForm({ ...addForm, amount: e.target.value })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded text-right"
                    />
                  </td>
                  <td className="text-center">
                    <input
                      type="date"
                      value={addForm.date}
                      onChange={(e) => setAddForm({ ...addForm, date: e.target.value })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </td>
                  <td className="text-center">
                    <select
                      value={addForm.type}
                      onChange={(e) => setAddForm({ ...addForm, type: e.target.value as 'income' | 'expense' })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    >
                      <option value="expense">💸 Expense</option>
                      <option value="income">💰 Income</option>
                    </select>
                  </td>
                  <td className="text-center">
                    <div className="flex space-x-1 justify-center">
                      <button
                        onClick={saveNew}
                        className="btn-secondary text-xs px-2 py-1"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelAdding}
                        className="btn-neutral text-xs px-2 py-1"
                      >
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


export default PaymentManager;